import { useEffect, useState } from "react";
import { Plus, Check, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fdate } from "@/lib/api";
import { PageHeader, StatusBadge, TableWrap, Th, Td, Empty, Field, NSelect } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const TYPES = ["Congé annuel", "Congé maladie", "Congé de circonstance", "Congé sans solde", "Absence justifiée"];
const days = (a, b) => Math.round((new Date(b) - new Date(a)) / 86400000) + 1;

export default function Leaves() {
  const [items, setItems] = useState([]);
  const [emps, setEmps] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ employee_id: "", type: TYPES[0], start_date: "", end_date: "", reason: "" });
  const load = () => api.get("/leaves").then((r) => setItems(r.data));
  useEffect(() => { load(); api.get("/employees").then((r) => setEmps(r.data)); }, []);

  const save = async () => {
    try { await api.post("/leaves", f); toast.success("Demande de congé enregistrée"); setOpen(false); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const decide = async (l, status) => {
    await api.post(`/leaves/${l.id}/decision`, { status });
    toast.success(status === "approuve" ? "Congé approuvé" : "Congé refusé");
    load();
  };

  return (
    <div>
      <PageHeader kicker="Gestion du personnel" title="Congés & absences">
        <Button className="bg-red-600 hover:bg-red-700" onClick={() => setOpen(true)} data-testid="new-leave-btn"><Plus className="mr-1 h-4 w-4" /> Nouvelle demande</Button>
      </PageHeader>
      {items.length ? (
        <TableWrap>
          <thead><tr><Th>Employé</Th><Th>Type</Th><Th>Période</Th><Th>Jours</Th><Th>Motif</Th><Th>Statut</Th><Th className="text-right">Décision</Th></tr></thead>
          <tbody>
            {items.map((l) => (
              <tr key={l.id} data-testid={`leave-row-${l.id}`} className="hover:bg-slate-50">
                <Td className="font-medium">{l.employee_name}</Td>
                <Td>{l.type}</Td>
                <Td>{fdate(l.start_date)} → {fdate(l.end_date)}</Td>
                <Td className="font-mono">{days(l.start_date, l.end_date)}</Td>
                <Td className="max-w-xs truncate text-slate-600">{l.reason || "—"}</Td>
                <Td><StatusBadge status={l.status} testId={`leave-status-${l.id}`} /></Td>
                <Td className="text-right">
                  {l.status === "demande" && (
                    <div className="flex justify-end gap-1.5">
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => decide(l, "approuve")} data-testid={`leave-approve-${l.id}`}><Check className="h-3.5 w-3.5" /></Button>
                      <Button size="sm" variant="outline" className="text-red-600" onClick={() => decide(l, "refuse")} data-testid={`leave-refuse-${l.id}`}><X className="h-3.5 w-3.5" /></Button>
                    </div>
                  )}
                </Td>
              </tr>
            ))}
          </tbody>
        </TableWrap>
      ) : <Empty testId="leaves-empty" text="Aucune demande de congé." />}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="leave-dialog">
          <DialogHeader><DialogTitle>Nouvelle demande de congé</DialogTitle></DialogHeader>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Employé *" className="sm:col-span-2">
              <NSelect value={f.employee_id} onChange={(v) => setF({ ...f, employee_id: v })} testId="leave-employee-select">
                <option value="">— Choisir —</option>{emps.map((e) => <option key={e.id} value={e.id}>{e.first_name} {e.last_name}</option>)}
              </NSelect>
            </Field>
            <Field label="Type" className="sm:col-span-2"><NSelect value={f.type} onChange={(v) => setF({ ...f, type: v })} testId="leave-type-select">{TYPES.map((t) => <option key={t}>{t}</option>)}</NSelect></Field>
            <Field label="Du *"><Input type="date" value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })} data-testid="leave-start-input" /></Field>
            <Field label="Au *"><Input type="date" value={f.end_date} onChange={(e) => setF({ ...f, end_date: e.target.value })} data-testid="leave-end-input" /></Field>
            <Field label="Motif" className="sm:col-span-2"><Input value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} data-testid="leave-reason-input" /></Field>
          </div>
          <DialogFooter><Button className="bg-red-600 hover:bg-red-700" onClick={save} data-testid="leave-save-btn">Enregistrer</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
